import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, KeyRound, Power, Pencil, Trash2, ShieldCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { ROLE_LABELS, useAuth } from "@/context/AuthContext";

export default function Admin() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [permsUser, setPermsUser] = useState(null);
  const [approveUser, setApproveUser] = useState(null);

  const load = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? "User disabled" : "User enabled");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteUser = async (u) => {
    if (u.id === me?.id) { toast.error("You cannot delete your own account"); return; }
    if (!window.confirm(`Permanently delete ${u.name} (${u.email})? Their assignments will be detached.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6 md:p-8 space-y-4" data-testid="admin-page">
      <div className="flex justify-between items-end">
        <div>
          <div className="mptg-overline">System</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Admin · Users</h1>
          <p className="text-slate-500 mt-1">{users.length} team member{users.length === 1 ? "" : "s"}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#2E86C1] hover:bg-[#2471A3] text-white" data-testid="new-user-btn">
              <Plus className="w-4 h-4 mr-1" /> New user
            </Button>
          </DialogTrigger>
          <NewUserDialog onDone={() => { setOpen(false); load(); }} />
        </Dialog>
      </div>
      <div className="mptg-card p-0 overflow-hidden">
        <table className="w-full mptg-table">
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Reports to</th><th>Branches</th><th>Regions</th><th>Status</th><th className="text-right">Actions</th></tr></thead>
          <tbody>
            {users.map((u) => {
              const isPending = u.pending_role || !u.role;
              return (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td className="font-medium">
                  {u.name}
                  {(u.auth_methods || []).includes("google") && (
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-[#2E86C1] font-semibold">Google</span>
                  )}
                </td>
                <td className="text-slate-600">{u.email}</td>
                <td>
                  {isPending ? (
                    <span className="badge-amber rounded px-2 py-0.5 text-xs font-semibold inline-flex items-center gap-1">
                      <ShieldAlert className="w-3 h-3" /> Pending approval
                    </span>
                  ) : (
                    <span className="badge-blue rounded px-2 py-0.5 text-xs font-semibold">{ROLE_LABELS[u.role] || u.role}</span>
                  )}
                </td>
                <td className="text-slate-600 text-xs">{u.reports_to_name || <span className="text-slate-400">—</span>}</td>
                <td className="text-slate-600 text-xs">{((u.branch_ids && u.branch_ids.length) ? `${u.branch_ids.length} branch${u.branch_ids.length === 1 ? "" : "es"}` : (u.branches || []).join(", ")) || "-"}</td>
                <td className="text-slate-600 text-xs">{(u.regions || []).join(", ") || "-"}</td>
                <td>{u.is_active ? <span className="badge-green rounded px-2 py-0.5 text-xs font-semibold">Active</span> : <span className="badge-slate rounded px-2 py-0.5 text-xs font-semibold">Disabled</span>}</td>
                <td className="text-right space-x-3 whitespace-nowrap">
                  {isPending && (
                    <button onClick={() => setApproveUser(u)} className="text-[#1E8449] hover:underline text-xs inline-flex items-center gap-1 font-semibold" data-testid={`approve-user-${u.id}`}>
                      <ShieldCheck className="w-3.5 h-3.5" /> Approve
                    </button>
                  )}
                  {!isPending && (
                    <button onClick={() => setEditUser(u)} className="text-[#2E86C1] hover:underline text-xs inline-flex items-center gap-1" data-testid={`edit-user-${u.id}`}>
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </button>
                  )}
                  {!isPending && (
                    <button onClick={() => setPermsUser(u)} className="text-[#2E86C1] hover:underline text-xs inline-flex items-center gap-1" data-testid={`perms-user-${u.id}`}>
                      <ShieldCheck className="w-3.5 h-3.5" /> Rights
                    </button>
                  )}
                  <button onClick={() => setResetUser(u)} className="text-[#2E86C1] hover:underline text-xs inline-flex items-center gap-1" data-testid={`reset-pwd-${u.id}`}>
                    <KeyRound className="w-3.5 h-3.5" /> Reset password
                  </button>
                  <button onClick={() => toggleActive(u)} className={`${u.is_active ? "text-[#C0392B]" : "text-[#1E8449]"} hover:underline text-xs inline-flex items-center gap-1`} data-testid={`toggle-active-${u.id}`}>
                    <Power className="w-3.5 h-3.5" /> {u.is_active ? "Disable" : "Enable"}
                  </button>
                  {u.id !== me?.id && (
                    <button onClick={() => deleteUser(u)} className="text-[#C0392B] hover:underline text-xs inline-flex items-center gap-1" data-testid={`delete-user-${u.id}`}>
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  )}
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>

      {resetUser && (
        <ResetPasswordDialog user={resetUser} onClose={() => setResetUser(null)} onDone={() => { setResetUser(null); load(); }} />
      )}

      {editUser && (
        <EditUserDialog user={editUser} allUsers={users} onClose={() => setEditUser(null)} onDone={() => { setEditUser(null); load(); }} />
      )}

      {permsUser && (
        <PermissionsDialog user={permsUser} onClose={() => setPermsUser(null)} onDone={() => { setPermsUser(null); load(); }} />
      )}

      {approveUser && (
        <ApproveUserDialog user={approveUser} allUsers={users} onClose={() => setApproveUser(null)} onDone={() => { setApproveUser(null); load(); }} />
      )}
    </div>
  );
}

function ResetPasswordDialog({ user, onClose, onDone }) {
  const [pwd, setPwd] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (pwd.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      await api.patch(`/users/${user.id}`, { password: pwd });
      toast.success(`Password reset for ${user.name}`);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="reset-password-dialog">
        <DialogHeader><DialogTitle>Reset password — {user.name}</DialogTitle></DialogHeader>
        <div className="py-2 space-y-2">
          <Label>New password</Label>
          <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="reset-pwd-input" />
          <div className="text-xs text-slate-500">The user will need to use this new password on next sign-in. Share it via a secure channel.</div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} className="bg-[#2E86C1] hover:bg-[#2471A3] text-white" data-testid="reset-pwd-save">
            {saving ? "Saving…" : "Reset"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewUserDialog({ onDone }) {
  const [f, setF] = useState({
    email: "", name: "", role: "process_manager",
    branch_ids: [], regions: "", reports_to: "",
    password: "", is_active: true,
  });
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  useEffect(() => {
    Promise.all([api.get("/branches"), api.get("/users")]).then(([b, u]) => {
      setBranches(b.data); setUsers(u.data);
    });
  }, []);
  const reportsToMap = {
    process_manager: "branch_manager",
    branch_manager: "regional_manager",
    regional_manager: "coo_ceo",
  };
  const suggestedRole = reportsToMap[f.role];
  // Admin can free-pick any active user as manager. We surface a "suggested"
  // group (typical next-level role) first, but any active non-self user is allowed.
  const activeUsers = users.filter((u) => u.is_active);
  const suggested = activeUsers.filter((u) => u.role === suggestedRole);
  const suggestedIds = new Set(suggested.map((u) => u.id));
  const others = activeUsers.filter((u) => !suggestedIds.has(u.id));

  const toggleBranch = (bid) => {
    setF((prev) => ({
      ...prev,
      branch_ids: prev.branch_ids.includes(bid)
        ? prev.branch_ids.filter((x) => x !== bid)
        : [...prev.branch_ids, bid],
    }));
  };

  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.email || !f.name || !f.password) { toast.error("Email, name, password required"); return; }
    setSaving(true);
    try {
      await api.post("/users", {
        email: f.email, name: f.name, role: f.role,
        branch_ids: f.branch_ids,
        branches: [],
        regions: f.regions.split(",").map((x) => x.trim()).filter(Boolean),
        reports_to: f.reports_to || null,
        password: f.password, is_active: f.is_active,
      });
      toast.success("User created"); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <DialogContent className="max-w-2xl">
      <DialogHeader><DialogTitle>New user</DialogTitle></DialogHeader>
      <div className="grid grid-cols-2 gap-3 py-2">
        <div><Label>Name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
        <div><Label>Email *</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="new-user-email" /></div>
        <div><Label>Role</Label>
          <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v, reports_to: "" })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label>Password *</Label><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
        {f.role !== "coo_ceo" && (
          <div className="col-span-2">
            <Label>
              Reports to
              <span className="text-slate-400 text-xs ml-2">
                {suggestedRole ? `— any active user (suggested role: ${ROLE_LABELS[suggestedRole]})` : "— any active user"}
              </span>
            </Label>
            <Select value={f.reports_to || "none"} onValueChange={(v) => setF({ ...f, reports_to: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="reports-to-select"><SelectValue placeholder="— unassigned —" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— unassigned —</SelectItem>
                {suggested.length > 0 && (
                  <>
                    <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-400">Suggested ({ROLE_LABELS[suggestedRole]})</div>
                    {suggested.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>)}
                  </>
                )}
                {others.length > 0 && (
                  <>
                    <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-400 border-t border-slate-100 mt-1">Other roles</div>
                    {others.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {ROLE_LABELS[u.role]}</SelectItem>)}
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
        )}
        {["process_manager", "branch_manager"].includes(f.role) && (
          <div className="col-span-2">
            <Label>Branches ({f.role === "process_manager" ? "where PM delivers" : "managed branches"})</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {branches.map((b) => (
                <button key={b.id} type="button" onClick={() => toggleBranch(b.id)}
                  className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
                    f.branch_ids.includes(b.id)
                      ? "bg-[#2E86C1] text-white border-[#2E86C1]"
                      : "bg-white text-slate-600 border-slate-200 hover:border-[#2E86C1]"
                  }`}
                  data-testid={`branch-chip-${b.code}`}
                >
                  {b.code} · {b.name}
                </button>
              ))}
              {branches.length === 0 && <span className="text-xs text-slate-400">No branches yet — create in Branches page.</span>}
            </div>
          </div>
        )}
        {f.role === "regional_manager" && (
          <div className="col-span-2">
            <Label>Regions (comma separated)</Label>
            <Input value={f.regions} onChange={(e) => setF({ ...f, regions: e.target.value })} placeholder="West, North" />
          </div>
        )}
      </div>
      <DialogFooter>
        <Button onClick={save} disabled={saving} className="bg-[#2E86C1] hover:bg-[#2471A3] text-white" data-testid="save-user-btn">Save</Button>
      </DialogFooter>
    </DialogContent>
  );
}

function EditUserDialog({ user, allUsers, onClose, onDone }) {
  const [f, setF] = useState({
    name: user.name || "",
    role: user.role,
    branch_ids: user.branch_ids || [],
    regions: (user.regions || []).join(", "),
    reports_to: user.reports_to || "",
    is_active: user.is_active,
  });
  const [branches, setBranches] = useState([]);
  useEffect(() => { api.get("/branches").then(({ data }) => setBranches(data)); }, []);
  const toggleBranch = (bid) => {
    setF((p) => ({
      ...p,
      branch_ids: p.branch_ids.includes(bid)
        ? p.branch_ids.filter((x) => x !== bid)
        : [...p.branch_ids, bid],
    }));
  };
  const [saving, setSaving] = useState(false);

  const reportsToMap = {
    process_manager: "branch_manager",
    branch_manager: "regional_manager",
    regional_manager: "coo_ceo",
  };
  const suggestedRole = reportsToMap[f.role];
  const activeUsers = allUsers.filter((u) => u.is_active && u.id !== user.id);
  const suggested = activeUsers.filter((u) => u.role === suggestedRole);
  const suggestedIds = new Set(suggested.map((u) => u.id));
  const others = activeUsers.filter((u) => !suggestedIds.has(u.id));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: f.name,
        role: f.role,
        branch_ids: f.branch_ids,
        regions: f.regions.split(",").map((x) => x.trim()).filter(Boolean),
        reports_to: f.reports_to || null,
        is_active: f.is_active,
      };
      await api.patch(`/users/${user.id}`, payload);
      toast.success("User updated");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="edit-user-dialog">
        <DialogHeader><DialogTitle>Edit user — {user.email}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="edit-user-name" /></div>
          <div><Label>Role / designation</Label>
            <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v, reports_to: "" })}>
              <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {f.role !== "coo_ceo" && (
            <div className="col-span-2">
              <Label>
                Reports to
                <span className="text-slate-400 text-xs ml-2">
                  {suggestedRole ? `— any active user (suggested role: ${ROLE_LABELS[suggestedRole]})` : "— any active user"}
                </span>
              </Label>
              <Select value={f.reports_to || "none"} onValueChange={(v) => setF({ ...f, reports_to: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="edit-reports-to"><SelectValue placeholder="— unassigned —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— unassigned —</SelectItem>
                  {suggested.length > 0 && (
                    <>
                      <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-400">Suggested ({ROLE_LABELS[suggestedRole]})</div>
                      {suggested.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>)}
                    </>
                  )}
                  {others.length > 0 && (
                    <>
                      <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-400 border-t border-slate-100 mt-1">Other roles</div>
                      {others.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {ROLE_LABELS[u.role]}</SelectItem>)}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
          )}
          {["process_manager", "branch_manager"].includes(f.role) && (
            <div className="col-span-2">
              <Label>Branches</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {branches.map((b) => (
                  <button key={b.id} type="button" onClick={() => toggleBranch(b.id)}
                    className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
                      f.branch_ids.includes(b.id)
                        ? "bg-[#2E86C1] text-white border-[#2E86C1]"
                        : "bg-white text-slate-600 border-slate-200 hover:border-[#2E86C1]"
                    }`}
                    data-testid={`edit-branch-chip-${b.code}`}
                  >
                    {b.code} · {b.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          {f.role === "regional_manager" && (
            <div className="col-span-2">
              <Label>Regions (comma separated)</Label>
              <Input value={f.regions} onChange={(e) => setF({ ...f, regions: e.target.value })} placeholder="West, North" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} className="bg-[#2E86C1] hover:bg-[#2471A3] text-white" data-testid="save-edit-user">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}




function PermissionsDialog({ user, onClose, onDone }) {
  const [matrix, setMatrix] = useState(null);
  const [overrides, setOverrides] = useState(user.permission_overrides || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/auth/permissions/matrix").then(({ data }) => setMatrix(data));
  }, []);

  const roleDefaults = matrix ? new Set(matrix.defaults[user.role] || []) : new Set();

  const cellState = (key) => {
    if (key in overrides) return overrides[key] ? "grant" : "deny";
    return roleDefaults.has(key) ? "default-on" : "default-off";
  };

  const cycle = (key) => {
    const cur = cellState(key);
    const next = { ...overrides };
    if (cur === "default-on") next[key] = false; // explicit deny
    else if (cur === "deny") delete next[key];     // back to default-off
    else if (cur === "default-off") next[key] = true;  // explicit grant
    else delete next[key];                              // grant -> default-on
    setOverrides(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/users/${user.id}/permissions`, { permission_overrides: overrides });
      toast.success("Permissions updated");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="permissions-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#2E86C1]" />
            Module rights — {user.name}
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-slate-500 mb-3">
          Click a cell to cycle: <span className="font-semibold">role-default → grant / deny → reset</span>.
          Defaults come from the user's role <span className="badge-blue rounded px-1.5 py-0.5 mx-1 text-[10px] font-bold">{ROLE_LABELS[user.role]}</span>;
          overrides take effect on top.
        </div>
        {!matrix ? (
          <div className="py-10 text-center text-slate-400">Loading matrix…</div>
        ) : (
          <div className="max-h-[480px] overflow-auto border border-slate-200 rounded">
            <table className="w-full text-sm" data-testid="permissions-matrix">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="text-left">
                  <th className="px-3 py-2">Module</th>
                  {matrix.actions.map((a) => (
                    <th key={a} className="px-3 py-2 text-center capitalize text-xs">{a}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrix.modules.map((m) => (
                  <tr key={m} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-semibold text-slate-700 capitalize">{m.replace("_", " ")}</td>
                    {matrix.actions.map((a) => {
                      const key = `${m}.${a}`;
                      const state = cellState(key);
                      const cls = {
                        "default-on": "bg-emerald-50 text-emerald-700 border-emerald-200",
                        "default-off": "bg-slate-50 text-slate-300 border-slate-200",
                        "grant": "bg-emerald-100 text-emerald-800 border-emerald-300 font-bold",
                        "deny": "bg-rose-100 text-rose-700 border-rose-300 font-bold",
                      }[state];
                      const label = {
                        "default-on": "✓",
                        "default-off": "·",
                        "grant": "+ Granted",
                        "deny": "× Denied",
                      }[state];
                      return (
                        <td key={a} className="px-3 py-1 text-center">
                          <button
                            type="button"
                            onClick={() => cycle(key)}
                            className={`w-full border rounded px-2 py-1 text-xs ${cls} hover:opacity-80 transition-opacity`}
                            data-testid={`perm-cell-${key}`}
                            title={key}
                          >
                            {label}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-slate-200">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#2E86C1] hover:bg-[#2471A3] text-white" data-testid="save-permissions-btn">
            {saving ? "Saving…" : "Save rights"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApproveUserDialog({ user, allUsers, onClose, onDone }) {
  const [role, setRole] = useState("branch_manager");
  const [reportsTo, setReportsTo] = useState("none");
  const [branches, setBranches] = useState([]);
  const [allBranches, setAllBranches] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/branches").then(({ data }) => setAllBranches(data));
  }, []);

  const possibleManagers = allUsers.filter(
    (u) => u.is_active && u.id !== user.id && (
      role === "process_manager" ? u.role === "branch_manager" : u.role !== role
    ),
  );

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/users/${user.id}/approve`, {
        role,
        reports_to: reportsTo === "none" ? null : reportsTo,
        branch_ids: branches,
      });
      toast.success("User approved & activated");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="approve-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#1E8449]" />
            Approve user — {user.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-2">
            {user.email} signed in via Google. Pick a role + scope to grant access.
          </div>
          <div>
            <Label>Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="approve-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Reports to</Label>
            <Select value={reportsTo} onValueChange={setReportsTo}>
              <SelectTrigger data-testid="approve-reports-to"><SelectValue placeholder="— none —" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— none —</SelectItem>
                {possibleManagers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} · {ROLE_LABELS[m.role]}</SelectItem>)}
              </SelectContent>
            </Select>
            {role === "process_manager" && (
              <div className="text-[11px] text-slate-400 mt-1">PMs must report to a Branch Manager.</div>
            )}
          </div>
          {(role === "branch_manager" || role === "process_manager") && (
            <div>
              <Label>Assigned branches</Label>
              <div className="max-h-32 overflow-auto border border-slate-200 rounded p-2 space-y-1">
                {allBranches.map((b) => (
                  <label key={b.id} className="flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={branches.includes(b.id)}
                      onChange={(e) => {
                        if (e.target.checked) setBranches([...branches, b.id]);
                        else setBranches(branches.filter((x) => x !== b.id));
                      }}
                      data-testid={`approve-branch-${b.id}`}
                    />
                    {b.code} · {b.name}
                  </label>
                ))}
                {allBranches.length === 0 && <div className="text-slate-400 text-xs">No branches yet.</div>}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-slate-200">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#1E8449] hover:bg-[#1a6e3e] text-white" data-testid="confirm-approve-btn">
            {saving ? "Approving…" : "Approve & activate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
